import { useEffect, useState } from "react";
import { If } from "shared/components/control-statements";
import Toggle2 from "shared/components/Toggle2";
import styles from "styles/shutdown-drawer.module.scss";
import { v4 as uuid } from "uuid";
import { EnvironmentsAPI } from "../../providers/EnvironmentsAPI";
import { Button } from "../../shared/components/Button";
import { Icon } from "../../shared/components/core/Icon";
import { Drawer } from "../../shared/components/Drawer";
import { Empty } from "../../shared/components/Empty";
import { Label } from "../../shared/components/Label";
import LazyInput from "../../shared/components/LazyInput";
import { EnvironmentStorage } from "../../shared/tools/EnvironmentStorage";

const executeOnSchema = (item, callback) => {
  for (let field of [
    "items",
    "extra_ingredients",
    "edges",
    "doughs",
    "sizes",
  ]) {
    if (item[field] && item[field].length > 0) {
      for (let children of item[field]) {
        executeOnSchema(children, callback);
        callback(children, item, field);
      }
    }
  }
};

export const ShutdownDrawer = ({ visible, catalog, onSave, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([]);

  const loadShutdowns = async () => {
    const environmentId = EnvironmentStorage.getSelectedEnvironment();
    setRules(
      (await EnvironmentsAPI.getConfigurationsByAlias(
        environmentId,
        "catalog_shutdown_rules",
        [],
      )) || [],
    );
  };

  useEffect(() => {
    if (visible) {
      loadShutdowns();
    }
  }, [visible]);

  const toggleRule = (ruleUUID) => {
    setRules(
      rules.map((r) => {
        if (ruleUUID == r.uuid) {
          r.contains = !r.contains;
        }
        return r;
      }),
    );
  };

  const deleteRule = (ruleUUID) => {
    setRules(rules.filter((r) => ruleUUID != r.uuid));
  };

  const handleSubmit = async () => {
    setLoading(true);

    const environmentId = EnvironmentStorage.getSelectedEnvironment();
    const response = await EnvironmentsAPI.putConfigurationsByAlias(
      environmentId,
      "catalog_shutdown_rules",
      {
        catalog_shutdown_rules: rules,
      },
    );

    if (response.getErrors([]).length > 0) {
      alert({ text: response.getErrors()[0], buttonText: "Ok" });
      setLoading(false);
      return false;
    }

    onSave && (await onSave(rules));
    onClose && onClose();
    loadShutdowns();
    setLoading(false);
  };

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      title="Ruptura de produtos"
      showBackButton
      backButton={onClose}
      showCloseButton={false}
      footer={
        <Button
          design="primary"
          className="w-100"
          disabled={loading}
          loading={loading}
          onClick={handleSubmit}
        >
          Salvar rupturas
        </Button>
      }
    >
      <LazyInput
        placeholder={"Informe o SKU ou nome do produto"}
        minTextLength={1}
        design="search"
        fillText={false}
        onOption={(option) => {
          const opt = {
            uuid: uuid(),
            sku: option.sku,
            name: option.name,
            contains: false,
          };
          setRules([opt, ...rules]);
        }}
        findOptions={async (text) => {
          const pSkus = [];

          if (!text) {
            return [];
          }

          executeOnSchema(catalog, (item, parent) => {
            const category = catalog.items.find((category) =>
              category.items.some((i) => i.sizes[0].sku === item.sku),
            );

            const categoryName = category?.name;

            const option = {
              name: `${item.name}`,
              category: categoryName,
              sku: item.sku,
            };

            if (item.type == "size") {
              if (item.sizes?.length > 0) {
                option.name = `${parent.name} / ${item.name}`;
              } else {
                option.name = `${parent.name} / ${categoryName}`;
              }
            }

            option.label = `${item.sku}`;

            if (item.sku) {
              if (item.sku.indexOf(text) > -1) {
                pSkus.push(option);
                return;
              }

              const lowerText = text.toLowerCase();
              if (item.name.toLowerCase().indexOf(lowerText) > -1) {
                pSkus.push(option);
                return;
              }

              if (
                item.type == "size" &&
                parent.name.toLowerCase().indexOf(lowerText) > -1
              ) {
                pSkus.push(option);
                return;
              }
            }
          });

          return Object.values(
            pSkus.reduce((t, i) => ({ ...t, [i.sku]: i }), {}),
          );
        }}
      />
      {rules.length == 0 && (
        <Empty>
          <Icon name="eye-slash" size="2x" light />
          <p>Você ainda não possui rupturas no cardápio</p>
          <span>
            Para adicionar, insira um código SKU ou o nome de um produto no
            campo acima.
          </span>
        </Empty>
      )}
      <If cond={rules.length > 0}>
        <Label className="mt-20">Rupturas ativas ({rules.length})</Label>

        {rules.map((rule) => {
          const category = catalog?.items?.find((category) =>
            category.items.some((item) => item.sizes[0].sku === rule.sku),
          );

          const categoryName = category?.name;
          return (
            <div className={styles.rupture}>
              <div className={styles.header}>
                <div className="d-flex align-center">
                  <p className={styles.sku}>{rule.sku}</p>
                  <div className="d-flex flex-column">
                    <p className={styles.name}>{rule.name}</p>
                    <p className={styles.category}>{categoryName}</p>
                  </div>
                </div>
                <div
                  className={styles.remove}
                  onClick={() => deleteRule(rule.uuid)}
                >
                  <Icon name="times" size="1x" />
                </div>
              </div>
              <Toggle2
                title="Ocultar produtos que tenham itens com esse SKU"
                checked={rule.contains}
                onChange={() => toggleRule(rule.uuid)}
              />
            </div>
          );
        })}
      </If>
    </Drawer>
  );
};
